<script setup>
/**
 * GviaSelectClient
 * Step 1: Client selection for payment collection
 */
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AppButton from '@/ui/components/AppButton.vue'
import GviaAddContactModal from './GviaAddContactModal.vue'
import { useGvia } from '@/core/composables/useGvia'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'

const route = useRoute()
const authStore = useAuthStore()
const { error: toastError, success: toastSuccess } = useToast()
const {
  clientData,
  contactSelected,
  loading,
  error,
  hasInvoices,
  fetchClientPayments,
  goToPayments,
  clearClient,
  sendTestReceiptEmail,
} = useGvia()

// Local state
const clientNumber = ref('')
const clientInputRef = ref(null)
const showEmailSelection = ref(false)
const showAddContactModal = ref(false)
const showTestPicker = ref(false)
const testTargetEmail = ref('')
const sendingTest = ref(false)
const isAdmin = computed(() => authStore.isAdmin)

// Get agent info
const agentName = computed(() => authStore.userDisplayName)
const agentNumber = computed(() => authStore.user?.profile?.agent || '—')

// Fetch client data
async function handleGetClient() {
  if (!clientNumber.value.trim()) {
    toastError('עליך להזין מספר לקוח')
    return
  }

  try {
    await fetchClientPayments(clientNumber.value.trim())
    showEmailSelection.value = false
  } catch (err) {
    toastError(err.message || 'שגיאה בטעינת נתוני לקוח')
  }
}

// Change client - clear and refocus
function handleChangeClient() {
  clearClient()
  showEmailSelection.value = false
  clientNumber.value = ''
  setTimeout(() => {
    clientInputRef.value?.focus()
  }, 100)
}

// Continue to email selection
function handleContinue() {
  showEmailSelection.value = true
}

// Proceed to invoices
// Note: clients without open invoices are allowed through — the agent can
// collect an upfront/manual amount in the next step
function handleProceedToInvoices() {
  if (contactSelected.value.length === 0) {
    toastError('עליך לבחור לפחות אימייל אחד לאישור')
    return
  }

  goToPayments()
}

// Toggle email selection
function toggleEmail(email) {
  const index = contactSelected.value.indexOf(email)
  if (index === -1) {
    contactSelected.value.push(email)
  } else {
    contactSelected.value.splice(index, 1)
  }
}

// Admin: send a sample receipt to one chosen contact
function openTestPicker() {
  testTargetEmail.value = clientData.value?.contactData?.[0]?.email || ''
  showTestPicker.value = true
}

async function handleSendTest() {
  if (!testTargetEmail.value || sendingTest.value) return
  sendingTest.value = true
  try {
    await sendTestReceiptEmail(testTargetEmail.value, {
      name: agentName.value,
      number: agentNumber.value,
    })
    toastSuccess(`מייל ניסיון נשלח אל ${testTargetEmail.value}`)
    showTestPicker.value = false
  } catch (err) {
    toastError(err.message || 'שליחת מייל הניסיון נכשלה')
  } finally {
    sendingTest.value = false
  }
}

// Handle contact added from modal — reload client data
async function handleContactAdded() {
  showAddContactModal.value = false
  try {
    await fetchClientPayments(clientNumber.value.trim())
  } catch (err) {
    toastError(err.message || 'שגיאה בטעינת נתוני לקוח')
  }
}

// Focus input on mount, or auto-fetch if clientMakat query param is present
onMounted(() => {
  const makat = route.query.clientMakat
  if (makat) {
    clientNumber.value = String(makat)
    handleGetClient()
  } else {
    setTimeout(() => {
      clientInputRef.value?.focus()
    }, 300)
  }
})
</script>

<template>
  <div class="select-client">
    <!-- Agent Info -->
    <div class="agent-info">
      <span class="agent-label">סוכן:</span>
      <span class="agent-name">{{ agentName }}</span>
      <span class="agent-number">({{ agentNumber }})</span>
    </div>

    <!-- Client Input -->
    <div class="input-section">
      <label class="input-label">
        <span>הזן מספר לקוח</span>
        <input
          ref="clientInputRef"
          v-model="clientNumber"
          type="text"
          class="client-input"
          placeholder="מספר לקוח"
          :disabled="loading"
          @keypress.enter="handleGetClient"
        />
      </label>

      <AppButton
        variant="primary"
        size="lg"
        :loading="loading"
        :disabled="!clientNumber.trim()"
        class="search-btn"
        @click="handleGetClient"
      >
        בחר לקוח
      </AppButton>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error-message">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span>{{ error }}</span>
    </div>

    <!-- Client Details Card -->
    <Transition name="fade-slide">
      <div v-if="clientData" class="client-card">
        <div class="client-header">
          <h3>פרטי לקוח</h3>
          <AppButton variant="secondary" size="sm" @click="handleChangeClient">
            החלף לקוח
          </AppButton>
        </div>

        <div class="client-details">
          <div class="detail-row">
            <span class="detail-label">מספר לקוח:</span>
            <span class="detail-value">{{ clientData.clientNum }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">שם לקוח:</span>
            <span class="detail-value">{{ clientData.clientName }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">ח.פ:</span>
            <span class="detail-value">{{ clientData.clientVat || '—' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">חשבוניות פתוחות:</span>
            <span class="detail-value" :class="{ empty: !hasInvoices }">
              {{ clientData.paymentsData?.length || 0 }}
            </span>
          </div>
        </div>

        <!-- No-invoices hint: flow continues with manual amount -->
        <div v-if="!hasInvoices" class="no-invoices-hint">
          אין חשבוניות פתוחות ללקוח זה — ניתן לגבות סכום ידני בשלב הבא
        </div>

        <!-- Continue Button -->
        <div v-if="!showEmailSelection" class="card-actions">
          <AppButton variant="primary" size="lg" class="continue-btn" @click="handleContinue">
            המשך
          </AppButton>
        </div>
      </div>
    </Transition>

    <!-- Email Selection -->
    <Transition name="fade-slide">
      <div v-if="showEmailSelection && clientData" class="email-section">
        <div class="email-header">
          <button class="add-contact-btn" @click="showAddContactModal = true">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            הוסף איש קשר
          </button>
          <button
            v-if="isAdmin"
            class="test-mail-btn"
            :disabled="!clientData.contactData?.length"
            title="שליחת מייל ניסיון (מנהלים בלבד)"
            @click="openTestPicker"
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            בדיקה
          </button>
          <svg
            viewBox="0 0 24 24"
            width="40"
            height="40"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <h3>בחר אימיילים לאישור</h3>
          <p>בחר את כתובות האימייל שיקבלו אישור תשלום</p>
        </div>

        <div v-if="clientData.contactData?.length > 0" class="email-list">
          <label
            v-for="contact in clientData.contactData"
            :key="contact.email"
            class="email-option"
            :class="{ selected: contactSelected.includes(contact.email) }"
          >
            <input
              type="checkbox"
              :checked="contactSelected.includes(contact.email)"
              @change="toggleEmail(contact.email)"
            />
            <span class="email-text">{{
              contact.name ? `${contact.name} — ${contact.email}` : contact.email
            }}</span>
            <svg
              v-if="contactSelected.includes(contact.email)"
              class="check-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </label>
        </div>

        <div v-else class="no-emails">
          <p>לא נמצאו אנשי קשר עבור לקוח זה</p>
        </div>

        <AppButton
          variant="primary"
          size="lg"
          :disabled="contactSelected.length === 0"
          class="proceed-btn"
          @click="handleProceedToInvoices"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          עבור לחשבוניות
        </AppButton>
      </div>
    </Transition>

    <!-- Admin: Test Receipt Picker -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showTestPicker" class="test-overlay" @click.self="showTestPicker = false">
          <div class="test-dialog">
            <h3>שליחת מייל ניסיון</h3>
            <p>בחר איש קשר. יישלח אליו אישור תשלום לדוגמה (ללא נתונים אמיתיים).</p>
            <div class="test-list">
              <label
                v-for="contact in clientData?.contactData || []"
                :key="contact.email"
                class="test-option"
                :class="{ selected: testTargetEmail === contact.email }"
              >
                <input v-model="testTargetEmail" type="radio" :value="contact.email" />
                <span>{{
                  contact.name ? `${contact.name} — ${contact.email}` : contact.email
                }}</span>
              </label>
            </div>
            <div class="test-actions">
              <AppButton
                variant="secondary"
                :disabled="sendingTest"
                @click="showTestPicker = false"
              >
                ביטול
              </AppButton>
              <AppButton
                variant="primary"
                :disabled="!testTargetEmail || sendingTest"
                @click="handleSendTest"
              >
                {{ sendingTest ? 'שולח...' : 'שלח מייל ניסיון' }}
              </AppButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Add Contact Modal -->
    <GviaAddContactModal
      v-if="showAddContactModal"
      :client-makat="clientData?.clientNum"
      @close="showAddContactModal = false"
      @contact-added="handleContactAdded"
    />
  </div>
</template>

<style scoped>
.select-client {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  overflow-y: auto;
}

/* Agent Info */
.agent-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
}

.agent-label {
  color: var(--text-muted);
}

.agent-name {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.agent-number {
  color: var(--text-secondary);
}

/* Input Section */
.input-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.input-label {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.input-label span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.client-input {
  width: 100%;
  padding: var(--spacing-md);
  font-size: var(--font-size-lg);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  text-align: center;
  transition: border-color var(--transition-fast);
}

.client-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.client-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-btn {
  width: 100%;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius);
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

/* Client Card */
.client-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
}

.client-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.client-header h3 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.client-details {
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.detail-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.detail-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.detail-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.detail-value.empty {
  color: var(--color-warning);
}

.no-invoices-hint {
  margin: 0 var(--spacing-md) var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: var(--border-radius);
  font-size: var(--font-size-xs);
  color: var(--color-warning);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

.card-actions {
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

.continue-btn {
  width: 100%;
}

/* Email Section */
.email-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.email-header {
  position: relative;
  text-align: center;
}

.email-header svg {
  color: var(--color-primary);
  margin-bottom: var(--spacing-sm);
}

.email-header h3 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.email-header p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.email-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.email-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.email-option:hover {
  border-color: var(--color-primary);
}

.email-option.selected {
  background: rgba(40, 53, 72, 0.08);
  border-color: var(--color-primary);
}

.email-option input[type='checkbox'] {
  display: none;
}

.email-text {
  flex: 1;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  word-break: break-all;
}

.check-icon {
  color: var(--color-success);
}

.no-emails {
  text-align: center;
  padding: var(--spacing-lg);
  color: var(--text-muted);
}

.no-emails p {
  margin: 0 0 var(--spacing-md) 0;
}

.add-contact-btn {
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 6px 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-contact-btn:hover {
  border-color: var(--color-primary);
  background: rgba(40, 53, 72, 0.05);
}

.add-contact-btn:active {
  transform: scale(0.97);
}

.test-mail-btn {
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 6px 10px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px dashed var(--color-warning);
  border-radius: var(--border-radius);
  color: var(--color-warning);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.test-mail-btn:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.16);
}

.test-mail-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Test receipt picker */
.test-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
}

.test-dialog {
  width: 100%;
  max-width: 480px;
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  box-shadow: var(--shadow-xl, 0 20px 60px rgba(0, 0, 0, 0.3));
}

.test-dialog h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.test-dialog p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.test-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  max-height: 50vh;
  overflow-y: auto;
}

.test-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}

.test-option.selected {
  border-color: var(--color-primary);
  background: rgba(40, 53, 72, 0.05);
}

.test-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
}

.proceed-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
